import type { ReactNode } from 'react';
import Button from '@/components/UI/Button';
import Callout from '@/components/UI/Callout';
import Disclosure from '@/components/UI/Disclosure';
import Drawer from '@/components/UI/Drawer';
import AlphaAtlasEntityBreakdown from './AlphaAtlasEntityBreakdown';
import { StatList, StatRow } from '@/components/UI/StatRow';
import StatusBadge from '@/components/UI/StatusBadge';
import {
    type AlphaAtlasDuration,
    type AlphaAtlasOutcomeKind,
    type AlphaAtlasReportRow,
    type AlphaAtlasWindowRole,
    alphaAtlasDirectionLabel,
    CONDITION_KIND_META,
    describeConcentration,
    describeEvidenceBadge,
    describeWindowConsistency,
    validationTwinOf,
    WINDOW_CONSISTENCY_META,
    WINDOW_ROLE_META
} from './AlphaAtlasReportState';
import {
    describeCombination,
    formatCount,
    formatHorizon,
    formatMarketDate,
    formatMarketDateTime,
    formatPercentMicros,
    formatRatio,
    humanizeCatalogLabel,
    TONE_TEXT_CLASS,
    toneForMicros
} from './alphaAtlasFormat';

interface AlphaAtlasResultDrawerProps {
    row: AlphaAtlasReportRow | null;
    isOpen: boolean;
    windowRole: AlphaAtlasWindowRole;
    durationId: AlphaAtlasDuration;
    outcomeKind: AlphaAtlasOutcomeKind;
    /** Requester-bound exposure for revealed windows; forwarded to the entity breakdown query */
    exposureFingerprint: string | undefined;
    /** For bundle rows: the entities the bundle was computed over, so the breakdown stays within them */
    bundleEntityKeys: string[] | undefined;
    /** Replaces the drawer's row, e.g. when an entity is picked from the breakdown */
    onSelectRow: (row: AlphaAtlasReportRow) => void;
    onClose: () => void;
    onReveal: (row: AlphaAtlasReportRow) => void;
    /** Switches the page to the combination lookup for this row's trigger/context and coordinates. */
    onLookup: (row: AlphaAtlasReportRow) => void;
}

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
    <section>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">{title}</h4>
        {children}
    </section>
);

const Headline = ({ label, micros, signed = true }: { label: string; micros: number | null; signed?: boolean }) => (
    <div className="rounded-md border border-zinc-800 bg-zinc-900/70 p-3">
        <div className="text-xs text-zinc-500">{label}</div>
        <div className={`mt-1 text-lg font-semibold tabular-nums ${TONE_TEXT_CLASS[toneForMicros(micros)]}`}>
            {formatPercentMicros(micros, { signed })}
        </div>
    </div>
);

const Signed = ({ micros }: { micros: number | null }) => (
    <span className={`tabular-nums ${TONE_TEXT_CLASS[toneForMicros(micros)]}`}>{formatPercentMicros(micros, { signed: true })}</span>
);

const Fingerprint = ({ label, value }: { label: string; value: string | null }) =>
    value ? (
        <div>
            <div className="text-xs text-zinc-500">{label}</div>
            <code className="block select-all break-all font-mono text-xs text-zinc-300">{value}</code>
        </div>
    ) : null;

/** Full diagnostics for one result row, grouped so the headline, then the caveats, come first. */
const AlphaAtlasResultDrawer = ({
    row,
    isOpen,
    windowRole,
    durationId,
    outcomeKind,
    exposureFingerprint,
    bundleEntityKeys,
    onSelectRow,
    onClose,
    onReveal,
    onLookup
}: AlphaAtlasResultDrawerProps) => {
    const concentration = row ? describeConcentration(row) : [];
    const consistency = row ? describeWindowConsistency(row, validationTwinOf(row)) : 'unknown';
    const evidenceBadge = row ? describeEvidenceBadge(row) : null;
    const controlled = WINDOW_ROLE_META[windowRole].controlled;

    return (
        <Drawer
            footer={
                row && (
                    <>
                        <Button variant="outline-dark" onClick={onClose}>
                            Close
                        </Button>
                        <Button variant="outline-dark" onClick={() => onLookup(row)}>
                            Look up this hypothesis
                        </Button>
                        {controlled ? (
                            <Button variant="warning" onClick={() => onReveal(row)}>
                                Reveal full history…
                            </Button>
                        ) : (
                            <span className="self-center text-xs text-amber-300">Full history revealed · exploratory</span>
                        )}
                    </>
                )
            }
            isOpen={isOpen}
            title={row ? describeCombination(row) : 'Result'}
            width="xl"
            onClose={onClose}
        >
            {row && (
                <div className="space-y-6">
                    <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2 text-sm text-zinc-300">
                            <span>{CONDITION_KIND_META[row.conditionKind].label}</span>
                            <span className="text-zinc-600">·</span>
                            <span>{row.entityLabel}</span>
                            <span className="text-zinc-600">·</span>
                            <span>{alphaAtlasDirectionLabel(outcomeKind, row.direction)}</span>
                            <span className="text-zinc-600">·</span>
                            <span>{formatHorizon(row.horizonSeconds)} horizon</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <StatusBadge status={row.evidenceState} tone={evidenceBadge?.tone ?? 'neutral'}>
                                {evidenceBadge?.label}
                            </StatusBadge>
                            <StatusBadge status={row.windowRole} tone={controlled ? 'neutral' : 'warning'}>
                                {WINDOW_ROLE_META[row.windowRole].label}
                            </StatusBadge>
                            <StatusBadge status="survivor-biased" tone="neutral">
                                Survivor-biased · never promotable
                            </StatusBadge>
                        </div>
                        <p className="text-xs text-zinc-500">{evidenceBadge?.description}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                        <Headline label="Net return" micros={row.netMeanMicros} />
                        <Headline label="Edge vs random" micros={row.randomEdgeMeanMicros} />
                        {row.conditionKind === 'parentAndChild' && (
                            <Headline label="Lift vs trigger alone" micros={row.liftVsTriggerMicros} />
                        )}
                        {row.conditionKind === 'parentAndChild' && (
                            <Headline label="Lift vs context alone" micros={row.liftVsContextMicros} />
                        )}
                        <Headline label="Edge vs SPY" micros={row.benchmarkEdgeMeanMicros} />
                        <div className="rounded-md border border-zinc-800 bg-zinc-900/70 p-3">
                            <div className="text-xs text-zinc-500">Support</div>
                            <div className="mt-1 text-lg font-semibold tabular-nums text-zinc-100">{formatCount(row.supportCount)}</div>
                        </div>
                    </div>

                    {concentration.length > 0 && (
                        <Callout title="Concentrated support" variant="warning">
                            <ul className="list-disc pl-4">
                                {concentration.map((reason) => (
                                    <li key={reason}>{reason}</li>
                                ))}
                            </ul>
                        </Callout>
                    )}

                    <Section
                        title={
                            row.aggregation === 'bundle'
                                ? 'By entity (bundle members)'
                                : row.scope === 'pooled'
                                  ? 'By entity'
                                  : 'All monitored entities'
                        }
                    >
                        <AlphaAtlasEntityBreakdown
                            durationId={durationId}
                            outcomeKind={outcomeKind}
                            entityKeys={row.aggregation === 'bundle' ? bundleEntityKeys : undefined}
                            exposureFingerprint={exposureFingerprint}
                            row={row}
                            onSelectRow={onSelectRow}
                        />
                    </Section>

                    {row.windowRole !== 'selectionValidation' && (
                        <Section title="Selection validation (walk-forward)">
                            {row.validationEvidenceState === null ? (
                                <p className="text-sm text-zinc-500">No result for this hypothesis in the selection-validation window.</p>
                            ) : (
                                <div className="space-y-2">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <StatusBadge status={consistency} tone={WINDOW_CONSISTENCY_META[consistency].tone}>
                                            {WINDOW_CONSISTENCY_META[consistency].label}
                                        </StatusBadge>
                                        <span className="text-xs text-zinc-500">{WINDOW_CONSISTENCY_META[consistency].description}</span>
                                    </div>
                                    <StatList>
                                        <StatRow label="Net return" value={<Signed micros={row.validationNetMeanMicros} />} />
                                        <StatRow label="Edge vs random" value={<Signed micros={row.validationRandomEdgeMeanMicros} />} />
                                        {row.conditionKind === 'parentAndChild' && (
                                            <StatRow
                                                label="Lift vs trigger alone"
                                                value={<Signed micros={row.validationLiftVsTriggerMicros} />}
                                            />
                                        )}
                                        {row.conditionKind === 'parentAndChild' && (
                                            <StatRow
                                                label="Lift vs context alone"
                                                value={<Signed micros={row.validationLiftVsContextMicros} />}
                                            />
                                        )}
                                        <StatRow label="Support" value={formatCount(row.validationSupportCount ?? 0)} />
                                        <StatRow
                                            label="Evidence"
                                            value={
                                                describeEvidenceBadge({
                                                    aggregation: row.aggregation,
                                                    evidenceState: row.validationEvidenceState
                                                }).label
                                            }
                                        />
                                    </StatList>
                                </div>
                            )}
                        </Section>
                    )}

                    {row.conditionKind === 'parentAndChild' && (
                        <Section title="Comparators (same entity, direction, horizon, and window)">
                            <StatList>
                                <StatRow label="Net mean, trigger alone" value={<Signed micros={row.triggerOnlyNetMeanMicros} />} />
                                <StatRow
                                    label="Support, trigger alone"
                                    value={row.triggerOnlySupportCount === null ? '—' : formatCount(row.triggerOnlySupportCount)}
                                />
                                <StatRow label="Lift from adding the context" value={<Signed micros={row.liftVsTriggerMicros} />} />
                                <StatRow label="Net mean, context alone" value={<Signed micros={row.contextOnlyNetMeanMicros} />} />
                                <StatRow
                                    label="Support, context alone"
                                    value={row.contextOnlySupportCount === null ? '—' : formatCount(row.contextOnlySupportCount)}
                                />
                                <StatRow label="Lift from adding the trigger" value={<Signed micros={row.liftVsContextMicros} />} />
                            </StatList>
                        </Section>
                    )}

                    <div className="grid gap-6 sm:grid-cols-2">
                        <Section title="Returns">
                            <StatList>
                                <StatRow label="Gross mean" value={<Signed micros={row.grossMeanMicros} />} />
                                <StatRow label="Net mean (after costs)" value={<Signed micros={row.netMeanMicros} />} />
                                <StatRow label="Median" value={<Signed micros={row.medianReturnMicros} />} />
                                <StatRow label="Win rate" value={`${formatRatio(row.winRate)} (${formatCount(row.winCount)} wins)`} />
                                <StatRow
                                    label="Best / worst"
                                    value={`${formatPercentMicros(row.maximumReturnMicros, { signed: true })} / ${formatPercentMicros(row.minimumReturnMicros, { signed: true })}`}
                                />
                                <StatRow
                                    label="Mean favorable excursion"
                                    value={formatPercentMicros(row.meanMfeMicros, { signed: true })}
                                />
                                <StatRow label="Mean adverse excursion" value={formatPercentMicros(row.meanMaeMicros, { signed: true })} />
                            </StatList>
                        </Section>
                        <Section title="Uncertainty">
                            <StatList>
                                <StatRow
                                    label="95% interval (net)"
                                    value={`${formatPercentMicros(row.ciLowerMicros, { signed: true })} to ${formatPercentMicros(row.ciUpperMicros, { signed: true })}`}
                                />
                                <StatRow label="Standard error" value={formatPercentMicros(row.standardErrorMicros)} />
                                <StatRow label="Standard deviation" value={formatPercentMicros(row.standardDeviationMicros)} />
                            </StatList>
                        </Section>
                        <Section title="Support and coverage">
                            <StatList>
                                <StatRow label="Observations" value={formatCount(row.supportCount)} />
                                <StatRow label="Effective samples" value={formatCount(row.distinctEpisodeCount)} />
                                <StatRow label="Distinct dates" value={formatCount(row.distinctDateCount)} />
                                <StatRow label="Distinct entities" value={formatCount(row.distinctEntityCount)} />
                                <StatRow label="Missing outcomes" value={formatCount(row.missingCount)} />
                                <StatRow label="Immature outcomes" value={formatCount(row.immatureCount)} />
                                <StatRow label="Mature through" value={formatMarketDate(row.matureDecisionEndAtUnix)} />
                            </StatList>
                        </Section>
                        <Section title="Robustness">
                            <StatList>
                                <StatRow
                                    label="Largest entity share"
                                    value={row.scope === 'entity' ? 'Single entity' : formatRatio(row.maximumEntityShare, 0)}
                                />
                                <StatRow label="Largest date share" value={formatRatio(row.maximumDateShare, 0)} />
                                <StatRow label="Largest effective-sample share" value={formatRatio(row.maximumEpisodeShare, 0)} />
                                <StatRow
                                    label="Leave-one-entity-out range"
                                    value={
                                        row.leaveOneEntityOutMinMeanMicros === null
                                            ? '—'
                                            : `${formatPercentMicros(row.leaveOneEntityOutMinMeanMicros, { signed: true })} to ${formatPercentMicros(row.leaveOneEntityOutMaxMeanMicros, { signed: true })}`
                                    }
                                />
                                <StatRow
                                    label="Largest leave-one-out shift"
                                    value={formatPercentMicros(row.maximumLeaveOneEntityOutDeltaMicros)}
                                />
                            </StatList>
                        </Section>
                    </div>

                    <Section title="Window">
                        <StatList>
                            <StatRow label="Evidence window" value={WINDOW_ROLE_META[row.windowRole].label} />
                            <StatRow
                                label="Range"
                                value={`${formatMarketDateTime(row.windowStartAtUnix)} – ${formatMarketDateTime(row.windowEndAtUnix)}`}
                            />
                            <StatRow label="Exposure status" value={row.exposureStatus} />
                        </StatList>
                    </Section>

                    <Disclosure title="Lineage and identifiers" variant="plain">
                        <div className="space-y-3 pt-2">
                            {row.parentLabel && (
                                <div>
                                    <div className="text-xs text-zinc-500">Trigger key</div>
                                    <code className="block select-all break-all font-mono text-xs text-zinc-300">{row.parentLabel}</code>
                                    <div className="text-xs text-zinc-600">{humanizeCatalogLabel(row.parentLabel)}</div>
                                </div>
                            )}
                            {row.childLabel && (
                                <div>
                                    <div className="text-xs text-zinc-500">Context key</div>
                                    <code className="block select-all break-all font-mono text-xs text-zinc-300">{row.childLabel}</code>
                                    <div className="text-xs text-zinc-600">{humanizeCatalogLabel(row.childLabel)}</div>
                                </div>
                            )}
                            <Fingerprint label="Hypothesis fingerprint" value={row.hypothesisFingerprint} />
                            <Fingerprint label="Condition fingerprint" value={row.conditionFingerprint} />
                            <Fingerprint label="Row fingerprint" value={row.rowFingerprint} />
                            <Fingerprint label="Cost policy fingerprint" value={row.costPolicyFingerprint} />
                        </div>
                    </Disclosure>
                </div>
            )}
        </Drawer>
    );
};

export default AlphaAtlasResultDrawer;
